import {
	Box,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { DelayByShift } from "../models/DelayByShift";

interface ShiftData {
	shift: string;
	durationMin: number;
	eventCount: number;
}

interface CrewData {
	crew: string;
	durationMin: number;
	eventCount: number;
}

interface AggregateData {
	duration: number;
	count: number;
}

interface StackedBarPoint {
	value: number;
	eventCount: number;
}

interface StackedBarFormatterParams {
	axisValue: string;
	data: StackedBarPoint;
	marker: string;
	seriesName: string;
}

const SHIFT_COLORS = ["#4338ca", "#f97316", "#0891b2", "#65a30d"] as const;

export const DelaysCrewShiftBreakdown = ({
	delays,
	sectionName,
}: {
	delays: DelayByShift[];
	sectionName?: string | null;
}) => {
	const shiftMap = new Map<string, AggregateData>();
	const crewMap = new Map<string, AggregateData>();
	const crewShiftMap = new Map<string, AggregateData>();

	for (const delay of delays) {
		const shift = delay.Shift || "Unknown";
		const crew = delay.Crew || "Unknown";
		const duration = delay.Duration || 0;
		const crewShiftKey = `${shift}\u0000${crew}`;

		const existingShift = shiftMap.get(shift);
		if (existingShift) {
			existingShift.duration += duration;
			existingShift.count += 1;
		} else {
			shiftMap.set(shift, { duration, count: 1 });
		}

		const existingCrew = crewMap.get(crew);
		if (existingCrew) {
			existingCrew.duration += duration;
			existingCrew.count += 1;
		} else {
			crewMap.set(crew, { duration, count: 1 });
		}

		const existingCrewShift = crewShiftMap.get(crewShiftKey);
		if (existingCrewShift) {
			existingCrewShift.duration += duration;
			existingCrewShift.count += 1;
		} else {
			crewShiftMap.set(crewShiftKey, { duration, count: 1 });
		}
	}

	const shiftData: ShiftData[] = Array.from(shiftMap.entries())
		.map(([shift, data]) => ({
			shift,
			durationMin: data.duration / 60,
			eventCount: data.count,
		}))
		.sort((a, b) => b.durationMin - a.durationMin);
	const crewData: CrewData[] = Array.from(crewMap.entries())
		.map(([crew, data]) => ({
			crew,
			durationMin: data.duration / 60,
			eventCount: data.count,
		}))
		.sort((a, b) => b.durationMin - a.durationMin);
	const shifts = Array.from(shiftMap.keys()).sort();
	const crews = crewData.map((crew) => crew.crew);
	const stackedSeries = shifts.map((shift, index) => ({
		name: `Shift ${shift}`,
		type: "bar",
		stack: "duration",
		barMaxWidth: 42,
		itemStyle: { color: SHIFT_COLORS[index % SHIFT_COLORS.length] },
		data: crews.map((crew): StackedBarPoint => {
			const aggregate = crewShiftMap.get(`${shift}\u0000${crew}`);
			return {
				value: Number(((aggregate?.duration || 0) / 60).toFixed(1)),
				eventCount: aggregate?.count || 0,
			};
		}),
	}));
	const totalSeries = {
		name: "Total",
		type: "bar",
		barGap: "-100%",
		barMaxWidth: 42,
		silent: true,
		itemStyle: { color: "transparent" },
		data: crews.map((crew): StackedBarPoint => {
			const aggregate = crewMap.get(crew);
			return {
				value: Number(((aggregate?.duration || 0) / 60).toFixed(1)),
				eventCount: aggregate?.count || 0,
			};
		}),
		label: {
			show: true,
			position: "right",
			formatter: (params: StackedBarFormatterParams) =>
				`${params.data.value.toFixed(1)} min`,
			fontWeight: 600,
			color: "#424242",
		},
	};

	const chartOption = {
		title: {
			text: "Delay Duration by Crew and Shift",
			left: "center",
			textStyle: { fontSize: 13, fontWeight: "bold" },
		},
		legend: {
			top: 28,
			data: shifts.map((shift) => `Shift ${shift}`),
		},
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			formatter: (params: StackedBarFormatterParams[]) => {
				const segments = params.filter(
					(param) => param.seriesName !== "Total" && param.data.value > 0,
				);
				const details = segments
					.map(
						(segment) =>
							`${segment.marker}${segment.seriesName}: ${segment.data.value.toFixed(1)} min (${segment.data.eventCount} events)`,
					)
					.join("<br/>");
				const total = segments.reduce(
					(sum, segment) => sum + segment.data.value,
					0,
				);

				return `Crew ${params[0]?.axisValue || ""}<br/>${details}<br/><strong>Total: ${total.toFixed(1)} min</strong>`;
			},
		},
		grid: { top: 70, left: 65, right: 90, bottom: 50 },
		xAxis: {
			type: "value",
			name: "Minutes",
			nameLocation: "middle",
			nameGap: 35,
		},
		yAxis: {
			type: "category",
			data: crews,
			name: "Crew",
			nameLocation: "middle",
			nameGap: 40,
		},
		series: [...stackedSeries, totalSeries],
	};

	const renderTable = (
		title: string,
		rows: { label: string; durationMin: number; eventCount: number }[],
	) => (
		<Box sx={{ overflow: "hidden", width: "100%" }}>
			<Box
				sx={{
					px: 2,
					py: 0.75,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography
					variant="caption"
					fontWeight={600}
					color="text.secondary"
					sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
				>
					{title}
				</Typography>
			</Box>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{title === "By Shift" ? "Shift" : "Crew"}</TableCell>
						<TableCell align="right">Duration (min)</TableCell>
						<TableCell align="right">Events</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.label} hover>
							<TableCell>
								<Typography variant="body2" fontWeight={500}>
									{row.label}
								</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography variant="body2">
									{row.durationMin.toFixed(1)}
								</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography variant="body2">{row.eventCount}</Typography>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	);

	return (
		<Paper variant="outlined" sx={{ mb: 1 }}>
			<Box
				sx={{
					px: 2,
					py: 1,
					backgroundColor: (theme) => theme.palette.primary.main,
				}}
			>
				<Typography variant="subtitle2" sx={{ color: "white" }}>
					{sectionName ? `${sectionName} — ` : ""}Delay Breakdown by Crew &
					Shift
				</Typography>
			</Box>

			<Divider />

			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					minHeight: 360,
				}}
			>
				<Box
					sx={{
						width: { xs: "100%", md: "38%" },
						borderRight: { md: "1px solid" },
						borderBottom: { xs: "1px solid", md: "none" },
						borderColor: "divider",
					}}
				>
					{renderTable(
						"By Shift",
						shiftData.map((row) => ({
							label: row.shift,
							durationMin: row.durationMin,
							eventCount: row.eventCount,
						})),
					)}
					{renderTable(
						"By Crew",
						crewData.map((row) => ({
							label: row.crew,
							durationMin: row.durationMin,
							eventCount: row.eventCount,
						})),
					)}
				</Box>
				<Box sx={{ flex: 1, minHeight: 360, p: 1 }}>
					<ReactECharts option={chartOption} style={{ height: "100%" }} />
				</Box>
			</Box>
		</Paper>
	);
};
